import type React from "react";
import type { SiteSheet } from "./model";

/** A site at a glance: where it lives, what runs, its data, and what the config says about it. */
export function SheetSection({ sheet }: { sheet: SiteSheet }) {
  const inv = sheet.inventory;
  const rows: [string, React.ReactNode][] = [];
  if (inv?.client) rows.push(["Client", inv.client]);
  if (inv?.contact) rows.push(["Contact", inv.contact]);
  if (sheet.addresses.length > 0) {
    rows.push([
      sheet.addresses.length === 1 ? "Address" : "Addresses",
      <span className="sheet-list" key="a">
        {sheet.addresses.map((a) => (
          <a key={a} href={a} target="_blank" rel="noopener noreferrer">
            {a.replace(/^https?:\/\//, "")}
          </a>
        ))}
      </span>,
    ]);
  }
  if (sheet.repo) {
    rows.push([
      "Repository",
      sheet.repo.url ? (
        <a href={sheet.repo.url} target="_blank" rel="noopener noreferrer" key="r">
          {sheet.repo.name}
          {sheet.repo.branch && ` · ${sheet.repo.branch}`}
        </a>
      ) : (
        sheet.repo.name
      ),
    ]);
  }
  if (sheet.deployment)
    rows.push([
      "Kubernetes",
      <span className="mono" key="k">
        {sheet.deployment}
      </span>,
    ]);
  if (sheet.running)
    rows.push([
      "Running",
      <span className="mono" key="i">
        {sheet.running}
      </span>,
    ]);
  if (sheet.volumes.length > 0) {
    rows.push([
      "Volumes",
      <span className="sheet-list" key="v">
        {sheet.volumes.map((v) => (
          <span key={v.name}>
            <span className="mono">{v.name}</span> {v.size}
          </span>
        ))}
      </span>,
    ]);
  }
  if (sheet.backup) rows.push(["Last backup", `${sheet.backup.when} · ${sheet.backup.size}`]);
  if (sheet.certificate) rows.push(["Certificate", sheet.certificate]);
  if (inv?.notes)
    rows.push([
      "Notes",
      <span className="sheet-notes" key="n">
        {inv.notes}
      </span>,
    ]);
  if (inv && inv.links.length > 0) {
    rows.push([
      "Links",
      <span className="sheet-list" key="l">
        {inv.links.map((l) => (
          <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">
            {l.label}
          </a>
        ))}
      </span>,
    ]);
  }
  if (rows.length === 0) return null;
  return (
    <section className="detail-section">
      <h3 className="eyebrow">Site sheet</h3>
      <dl className="sheet">
        {rows.map(([label, value]) => (
          <div key={label} className="sheet-row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
